import React, { useCallback, useMemo, useState } from 'react';
import classNames from 'classnames';
import { ModalItem } from '@repo/components/modals';
import { useDispatch, useSelector } from 'react-redux';
import { mergeContactBatch } from '@views/CommandBar/actionsBySection/contactBatchActions';
import { ConnectedPill } from '@repo/components/containers';
import { CircledArrowIcon, LoaderIcon } from '@repo/icons';
import styles from './Merge.module.scss';
import Contact from '@repo/utils/models/Contact';
import { selectSelectedContact } from '@repo/redux/actions/ui';
import { OrderedMap, Set } from 'immutable';
import { selectMergingContactBatch } from '@repo/redux/actions/contacts';
import { ContactSearchModal } from '../ContactSearchModal';
import { metaTrigger } from '@repo/utils/helpers/keyboardHandler';

const MergeActionOption = () => (
  <div className={classNames(styles.mergeActionOption, styles.nonContactRow)}>
    <CircledArrowIcon className={styles.inlineIcon} color="textPrimary" />
    <span className="text-small">Merge People</span>
  </div>
);

const MergingIndicator = () => (
  <ModalItem className={styles.mergingIndicator}>
    <div className={styles.nonContactRow}>
      <LoaderIcon width={12} height={12} />
      <span className="text-small">Merging People</span>
    </div>
  </ModalItem>
);

export default function MergeModal({ closeModal }: { closeModal: () => void }) {
  const dispatch = useDispatch();
  const mergingContactBatch = useSelector(selectMergingContactBatch);
  // We memoize the selected contact when the modal mounts, in case the app UI state changes while the modal is up
  const currentSelectedContact = useSelector(selectSelectedContact);
  const selectedContact = useMemo(() => currentSelectedContact, []);

  // The selectedContact is the first member in our list of contacts selected for merge
  const [contactsSelectedForMerge, setContactsSelectedForMerge] = useState(
    OrderedMap([[selectedContact.id, selectedContact]]),
  );

  const onOptionSelected = useCallback(
    (option) => {
      if (mergingContactBatch) {
        return;
      }

      if (option.type === 'merge') {
        mergeContactBatch(Set<string>(contactsSelectedForMerge.keys()), 'Merge Modal', dispatch)();
      } else if (!contactsSelectedForMerge.has(option.id)) {
        setContactsSelectedForMerge(contactsSelectedForMerge.set(option.id, option));
      } else {
        setContactsSelectedForMerge(contactsSelectedForMerge.delete(option.id));
      }
    },
    [contactsSelectedForMerge, mergingContactBatch],
  );

  const adjustResults = useCallback(
    (results) =>
      contactsSelectedForMerge.size == 1 || mergingContactBatch
        ? results
        : results.concat([{ type: 'merge' }]),
    [contactsSelectedForMerge, mergingContactBatch],
  );

  const header = (
    <div className={styles.selected}>
      {contactsSelectedForMerge
        .valueSeq()
        .toArray()
        .map((contact: Contact) => {
          const removeable = contact.id != selectedContact.id;
          let onClick = () => {};

          if (removeable && !mergingContactBatch) {
            onClick = () =>
              setContactsSelectedForMerge(contactsSelectedForMerge.delete(contact.id));
          }

          return (
            <ConnectedPill
              key={contact.id}
              className={classNames(styles.selectedContact, removeable && styles.removeable)}
              contact={contact}
              editable={removeable}
              onClick={onClick}
            />
          );
        })}
    </div>
  );

  const onInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && metaTrigger(e)) {
      if (contactsSelectedForMerge.size > 1) {
        mergeContactBatch(Set<string>(contactsSelectedForMerge.keys()), 'Merge Modal', dispatch)();
      }

      e.stopPropagation();
      e.preventDefault();
      return true;
    }

    return false;
  };

  return (
    <ContactSearchModal
      isOpen={true}
      defaultContactSearchTerm={selectedContact.displayName}
      resultFilter={({ id }) => id != selectedContact.id}
      closeModal={closeModal}
      contactIsSelected={({ id }) => contactsSelectedForMerge.has(id)}
      adjustResults={adjustResults}
      renderNonContactOption={({ type }) => type && type == 'merge' && <MergeActionOption />}
      onOptionSelected={onOptionSelected}
      header={header}
      placeholder="Search for Duplicates"
      afterOptions={mergingContactBatch && <MergingIndicator />}
      onInputKeyDown={onInputKeyDown}
    />
  );
}
